import React, { Component } from 'react';

import * as clipboard from 'utilities/clipboard';
import { getEmbedCode, getResponsiveEmbedCode } from 'utilities/video';

import DropdownMenu from 'components/dropdown-menu';
import RadioGroup from 'components/radio-group';
import Button from 'components/ui-components/button';
import Input from 'components/ui-components/input';

import './styles.less';

const DEFAULT_EMBED_WIDTH = 1280;
const DEFAULT_EMBED_HEIGHT = 800;
const MAX_EMBED_WIDTH = 640;

const EMBED_OPTIONS = [
  {
    label: 'Responsive',
    value: 'responsive',
  },
  {
    label: 'Fixed Size',
    value: 'fixed',
  },
];

class EmbedCodeDropdown extends Component {
  state = {
    embedCopied: false,
    showEmbedCode: false,
    sizeType: 'responsive',
    initialHeight: DEFAULT_EMBED_HEIGHT,
    initialWidth: DEFAULT_EMBED_WIDTH,
    height: DEFAULT_EMBED_HEIGHT,
    width: DEFAULT_EMBED_WIDTH,
  };

  selectAndCopyEmbedCode = () => {
    if (!this.embedNode) {
      return;
    }

    const embedCopied = clipboard.copy(this.embedNode);

    this.setState({ embedCopied });

    if (this.copyTimeout) {
      window.clearTimeout(this.copyTimeout);
      this.copyTimeout = null;
    }

    this.copyTimeout = window.setTimeout(() => {
      this.setState({ embedCopied: false });
    }, 3000);
  };

  onSizeSelection = sizeType => {
    this.setState({
      sizeType,
      embedCopied: false,
    });
  };

  toggleEmbedCodeView = () =>
    this.setState({ showEmbedCode: !this.state.showEmbedCode });

  onMenuClick = ev => ev.stopPropagation();

  onWidthChange = width => {
    const { aspectRatio } = this.state;

    const height = Math.ceil(width / aspectRatio);

    this.setState({
      width,
      height,
    });
  };

  onHeightChange = height => {
    const { aspectRatio } = this.state;

    const width = Math.ceil(height * aspectRatio);

    this.setState({
      width,
      height,
    });
  };

  componentDidUpdate(prevProps) {
    if (!prevProps.embedDropdownOpen && this.props.embedDropdownOpen) {
      this.selectAndCopyEmbedCode();
    }
  }

  componentDidMount() {
    if (this.props.embedDropdownOpen) {
      this.selectAndCopyEmbedCode();
    }
  }

  static getDerivedStateFromProps(props, state) {
    const {
      width: videoWidth,
      height: videoHeight,
    } = props.video.video_properties;

    const shouldResetEmbedSize =
      !state.aspectRatio ||
      videoWidth !== state.initialWidth ||
      videoHeight !== state.initialHeight;

    if (!shouldResetEmbedSize) {
      return null;
    }

    const aspectRatio = videoWidth / videoHeight;

    let newWidth = videoWidth;
    let newHeight = videoHeight;

    if (videoWidth > MAX_EMBED_WIDTH) {
      newWidth = MAX_EMBED_WIDTH;
      newHeight = Math.ceil(newWidth / aspectRatio);
    }

    return {
      initialWidth: videoWidth,
      initialHeight: videoHeight,
      width: newWidth,
      height: newHeight,
      aspectRatio,
    };
  }

  render() {
    const { embedDropdownOpen, align, vAlign, video } = this.props;
    const {
      showEmbedCode,
      embedCopied,
      sizeType,
      width,
      height,
      aspectRatio,
    } = this.state;

    const isReponsive = sizeType === 'responsive';

    const embedCode = isReponsive
      ? getResponsiveEmbedCode(video.id, 1 / aspectRatio)
      : getEmbedCode(video.id, width, height);

    const disabledSizeInput = isReponsive ? true : false;
    const hiddenEmbedClass = showEmbedCode ? '' : ' hide-code';

    return (
      <DropdownMenu
        show={embedDropdownOpen}
        align={align}
        vAlign={vAlign}
        additionalClass="embed-dropdown"
      >
        <div
          className={`embed-dropdown-contents ${
            showEmbedCode ? 'code-expanded' : ''
          }`}
          onClick={this.onMenuClick}
        >
          <div
            className="embed-code-container"
            onClick={this.selectAndCopyEmbedCode}
          >
            <div
              className={`embed-code${hiddenEmbedClass}`}
              ref={embedNode => (this.embedNode = embedNode)}
            >
              {embedCode}
            </div>
          </div>

          <div className="embed-menu">
            <div className="embed-dropdown-title">Embed code</div>

            <RadioGroup
              groupClass="embed-radio-group"
              options={EMBED_OPTIONS}
              selectedValue={this.state.sizeType}
              onSelection={this.onSizeSelection}
            />

            <div className="embed-code-size-group">
              <Input
                fieldValue={this.state.width}
                fieldId="embed-code-width"
                fieldType="number"
                inputClass="embed-code-size-input"
                onChange={this.onWidthChange}
                disabled={disabledSizeInput}
              />

              <span className="embed-code-symbol">×</span>

              <Input
                fieldValue={this.state.height}
                fieldId="embed-code-height"
                fieldType="number"
                inputClass="embed-code-size-input"
                onChange={this.onHeightChange}
                disabled={disabledSizeInput}
              />
            </div>

            <Button
              buttonClass={`copy-embed-button ${
                embedCopied ? 'embed-copied' : ''
              }`}
              buttonType="rp-primary-4"
              value={embedCopied ? 'Code Copied!' : 'Copy Code'}
              onClicked={this.selectAndCopyEmbedCode}
            />

            <div
              className="embed-show-code-button"
              onClick={this.toggleEmbedCodeView}
            >
              {showEmbedCode ? 'Hide Code' : 'Preview Code'}
            </div>
          </div>
        </div>
      </DropdownMenu>
    );
  }
}

export default EmbedCodeDropdown;
